import React, {useEffect, useState} from 'react';
import {Alert, Badge, Button, Form, Spinner} from 'react-bootstrap';
import {getP2pCatalog, getP2pField, saveP2pField, p2pErrorMessage} from '../api/p2p';
import AdminP2pExecution from './admin-p2p-execution';
import '../css/admin-p2p.css';

const editableConnections = configuration => configuration.cameras.map(camera => {
  const saved = configuration.connections.find(connection => connection.cameraId === camera.id);
  if (saved) return {...saved, values: {...saved.values}};
  return {cameraId: camera.id, profileId: '', values: {}, secretsConfigured: {}};
});

export default function AdminP2pField({fieldId, token}) {
  const [saved, setSaved] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let disposed = false;
    setLoading(true);
    setError('');
    setSaved(null);
    setConnections([]);
    Promise.all([getP2pField(fieldId, token), getP2pCatalog(token)]).then(([configuration, models]) => {
      if (disposed) return;
      setSaved(configuration);
      setCatalog(models);
      setEnabled(configuration.enabled);
      setConnections(editableConnections(configuration));
    }).catch(err => { if (!disposed) setError(p2pErrorMessage(err)); })
      .finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; };
  }, [fieldId, token, reload]);

  const update = (cameraId, transform) => {
    setSuccess(false);
    setConnections(items => items.map(item => item.cameraId === cameraId ? transform(item) : item));
  };
  const chooseProfile = (cameraId, profileId) => {
    const profile = catalog.profiles.find(p => p.id === profileId);
    const values = {};
    if (profile) profile.fields.forEach(field => { if (field.default !== undefined) values[field.name] = field.default; });
    update(cameraId, current => ({...current, profileId, values, secretsConfigured: {}}));
  };
  const cancel = () => {
    setEnabled(saved.enabled);
    setConnections(editableConnections(saved));
    setError('');
    setSuccess(false);
  };
  const persist = async (nextEnabled, selected) => {
    setBusy(true);
    setError('');
    setSuccess(false);
    try {
      const result = await saveP2pField(fieldId, {
        enabled: nextEnabled, revision: saved.revision,
        connections: selected.map(({cameraId, profileId, values}) => ({cameraId, profileId, values}))
      }, token);
      setSaved(result);
      setEnabled(result.enabled);
      setConnections(editableConnections(result)); // Clear entered passwords after saving.
      setSuccess(true);
    } catch (err) {
      setError(p2pErrorMessage(err));
      // Discard typed secrets on failure as well; keep other inputs for correction.
      setConnections(items => items.map(item => {
        const profile = catalog.profiles.find(p => p.id === item.profileId);
        const values = {...item.values};
        (profile ? profile.fields : []).filter(f => f.secret).forEach(f => { delete values[f.name]; });
        return {...item, values};
      }));
    } finally { setBusy(false); }
  };
  const save = event => {
    event.preventDefault();
    return persist(enabled, enabled ? connections : saved.connections);
  };
  const returnToLocal = () => {
    // Discard only unsaved edits. Retain saved P2P credentials and never send
    // any NVR configuration through this independent endpoint.
    if (saved.enabled) return persist(false, saved.connections);
    cancel();
  };

  return <section className="p2p-field-panel" aria-label="Collegamento P2P del campo">
    <div className="p2p-field-heading"><h6>Registrazione del campo</h6>
      {saved && <Badge variant={saved.active ? 'success' : 'light'}>{saved.active ? 'P2P selezionato' : 'NVR locale'}</Badge>}
    </div>
    {loading ? <p role="status"><Spinner as="span" animation="border" size="sm"/> Caricamento configurazione…</p> : <>
      {error && <Alert variant="danger" role="alert">{error} <Button size="sm" variant="link" onClick={() => setReload(n => n + 1)}>Ricarica</Button></Alert>}
      {saved && catalog && <Form onSubmit={save} autoComplete="off">
        <fieldset disabled={busy || saved.active}>
          <Form.Check id={`p2p-enabled-${fieldId}`} type="switch" label="Usa P2P" checked={enabled}
            onChange={event => { setEnabled(event.target.checked); setSuccess(false); }}/>
          <p className="p2p-preservation-note">Le impostazioni dell’NVR locale restano salvate anche quando configuri il P2P.</p>
          {!enabled && <p className="p2p-help">Il campo utilizza l’NVR locale.</p>}
          {enabled && <>
            {!saved.active && <Alert variant="info" className="mt-3">
              <strong>P2P da attivare.</strong> Puoi salvare i dati delle telecamere. La registrazione resta sull’NVR locale fino all’attivazione del servizio P2P.
            </Alert>}
            {!catalog.encryptionAvailable && <Alert variant="warning">Il salvataggio protetto delle credenziali deve essere configurato prima di procedere.</Alert>}
            {saved.cameras.length === 0 && <Alert variant="warning">Aggiungi prima una telecamera al campo dalla gestione telecamere.</Alert>}
            {saved.cameras.map(camera => {
              const connection = connections.find(c => c.cameraId === camera.id);
              if (!connection) return null;
              const profile = catalog.profiles.find(p => p.id === connection.profileId);
              return <div className="p2p-camera-form" key={camera.id}>
                <h6>{camera.name}</h6>
                <Form.Group controlId={`p2p-model-${fieldId}-${camera.id}`}>
                  <Form.Label>Tipo di telecamera</Form.Label>
                  <Form.Control as="select" required value={connection.profileId} onChange={event => chooseProfile(camera.id, event.target.value)}>
                    <option value="">Seleziona marca e modello</option>
                    {catalog.profiles.map(model => <option key={model.id} value={model.id} disabled={!model.enabled || !model.configurable}>
                      {model.label}{!model.configurable || model.status === 'unverified' ? ' — da verificare' : !model.enabled ? ' — disabilitato' : ' — in prova'}
                    </option>)}
                  </Form.Control>
                </Form.Group>
                {profile && profile.fields.map(field => {
                  const stored = field.secret && connection.secretsConfigured[field.name];
                  const control = {name: `p2p-${camera.id}-${field.name}`, value: connection.values[field.name] || '',
                    required: field.required && !stored, maxLength: field.maxLength, pattern: field.pattern,
                    autoComplete: field.secret ? 'new-password' : 'off',
                    onChange: event => {
                      const value = event.target.value;
                      update(camera.id, c => ({...c, values: {...c.values, [field.name]: value}}));
                    }};
                  return <Form.Group key={field.name} controlId={`p2p-${fieldId}-${camera.id}-${field.name}`}>
                    <Form.Label>{field.label}</Form.Label>
                    {field.type === 'select' ? <Form.Control as="select" {...control}>
                      {field.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </Form.Control> : <Form.Control type={field.type} {...control} placeholder={stored ? 'Già salvata — lascia vuoto per mantenerla' : ''}/>}
                  </Form.Group>;
                })}
              </div>;
            })}
          </>}
          <div className="p2p-field-actions">
            <Button type="submit" size="sm" disabled={busy || (enabled && (!catalog.encryptionAvailable || !saved.cameras.length))}>
              {busy ? 'Salvataggio…' : 'Salva impostazioni P2P'}
            </Button>
            <Button type="button" size="sm" variant="outline-secondary" onClick={cancel}>Annulla modifiche</Button>
            {(enabled || saved.enabled) && <Button type="button" size="sm" variant="outline-primary" onClick={returnToLocal}>
              Torna all’NVR locale
            </Button>}
          </div>
          {(enabled || saved.enabled) && <p className="p2p-help">Tornando all’NVR locale conservi anche la configurazione P2P già salvata.</p>}
        </fieldset>
        {success && <p role="status" className="text-success mt-2 mb-0">{enabled ? 'Configurazione P2P salvata. In attesa di attivazione.' : 'Impostazioni salvate. Il campo usa l’NVR locale.'}</p>}
      </Form>}
      {saved && saved.execution && saved.execution.activationAvailable && <AdminP2pExecution
        fieldId={fieldId} token={token} saved={saved} onChanged={configuration => {
          setSaved(configuration); setEnabled(configuration.enabled);
          setConnections(editableConnections(configuration)); setSuccess(false); setError('');
        }}/>}
    </>}
  </section>;
}
